import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  CssBaseline,
  Drawer,
  List,
  Box,
  Button,
  Typography,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
  tooltipClasses,
} from "@mui/material";

import { LogoIcon } from "../../icons";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";

import Navbar from "../Navbar";
import Versioning from "../Versioning";

import { styled } from "@mui/material/styles";
import { colorStyling } from "../../helpers/color";
import { useRemoteConfig } from "../hooks";

import { parseVariables } from "../../helpers/localize";
import parser from "html-react-parser";
import { SET_AUTH_FEATURES } from "../../store/actionTypes/userActionType";
import { getOrganizationFeatures } from "../../store/actions/userAction";
import {
  sidebarMenuConfigBottom,
  sidebarMenuConfigTop,
} from "./sidebarMenuConfig";
import { externalSidebarConfig } from "../../externalFeatures/externalSidebarMenu";
import * as Sentry from "@sentry/react";
import { coreFeatureFlag, getGlobalKey } from "../../helpers/featureFlag";
import { getUserAuth } from "../../store/actions/authAction";
import { SET_SIDEBAR_OPEN } from "../../store/actionTypes/globalActionType";
import { TypographyNormal } from "../customComponent";

const drawerWidth = 240;

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.white,
  },
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: colorStyling.blue.deep,
    cursor: "pointer",
  },
  marginBottom: theme.spacing(1),
}));

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 400,
  marginLeft: theme.spacing(1.75),
  lineHeight: "1.625rem",
}));

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const Sidebar = ({ minified }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t: tCommon } = useTranslation();

  const { user, features } = useSelector((state) => state.auth);
  const userRole = user?.type;
  const userPermission = user?.permission;

  const globalFF = useRemoteConfig(getGlobalKey(), JSON.parse, coreFeatureFlag);

  const [openSidebar, setOpenSidebar] = useState(true);

  useEffect(() => {
    if (minified) {
      setTimeout(() => {
        setOpenSidebar(false);
      }, 444);
    }
  }, [minified]);

  const updateFeatureData = async (userID) => {
    if (!userID) {
      return;
    }

    let featureData;
    try {
      featureData = await getOrganizationFeatures(userID);
    } catch (error) {
      featureData = [];
    }

    const featureFlags = { ...globalFF };
    featureData.features.forEach((feature) => {
      featureFlags[feature] = false;
    });
    dispatch({ type: SET_AUTH_FEATURES, payload: featureFlags });
  };

  const refreshUserData = async () => {
    dispatch(
      getUserAuth((data) => {
        Sentry.setUser({
          id: data.id,
          name: data.name,
          username: data.name,
          email: data.email,
          mfaType: data.mfaType,
          loginAt: data.loginAt,
          roleType: data.type,
          organization: data.organization?.name || "null",
          userGroup: data.userGroup?.name || "null",
        });
      })
    );
  };

  useEffect(() => {
    if (user?.id) {
      refreshUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (globalFF?.enabled) {
      updateFeatureData(user?.organization?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFF]);

  const handlePage = (path) => {
    navigate(path);
  };

  const handleSidebarMenuButton = () => {
    const value = !openSidebar;
    setOpenSidebar(value);
    dispatch({ type: SET_SIDEBAR_OPEN, payload: value });
  };

  const itxcastLogo = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <LogoIcon />

      {openSidebar && (
        <Typography
          component="h2"
          sx={{
            color: colorStyling.white.full,
            marginLeft: "0.5rem",
            cursor: "pointer",
            fontSize: "2rem",
            fontWeight: 700,
            lineHeight: "2.375rem",
          }}
          onClick={() => navigate("/")}
        >
          {parser(
            parseVariables(tCommon("sidebar.title"), {
              color: colorStyling.blue.sky,
            })
          )}
        </Typography>
      )}
    </Box>
  );

  const menuItemUi = (item) => {
    const currentPath =
      pathname === item.path || pathname.includes(item.pathInclude);
    const MenuIcon = item.getIcon;
    return (
      <Box key={item.path}>
        <LightTooltip
          followCursor
          disableInteractive
          placement="bottom-start"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 14],
                  },
                },
              ],
            },
          }}
          title={
            openSidebar ? (
              ""
            ) : (
              <TypographyNormal sx={{ textAlign: "center" }}>
                {item.label}
              </TypographyNormal>
            )
          }
        >
          <StyledListItem
            dense
            onClick={() => handlePage(item.path)}
            sx={{
              justifyContent: openSidebar ? "initial" : "center",
              backgroundColor: currentPath ? colorStyling.blue.deep : null,
            }}
          >
            <Box
              display={"flex"}
              alignItems={"center"}
              sx={{ py: openSidebar ? 0 : "2px" }}
            >
              {MenuIcon ? <MenuIcon /> : item.icon}
            </Box>

            {openSidebar && (
              <StyledListItemText
                sx={{
                  color: currentPath
                    ? colorStyling.white.active
                    : colorStyling.white.inactive,
                }}
                primary={item.label}
              />
            )}
          </StyledListItem>
        </LightTooltip>
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />

        <Navbar />

        <StyledDrawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              backgroundColor: colorStyling.primary,
              paddingY: 3,
            },
          }}
          variant="permanent"
          open={openSidebar}
          anchor="left"
        >
          {itxcastLogo}

          <Box py={2}>
            {!openSidebar && <Divider />}

            <Box
              display={"flex"}
              justifyContent={!openSidebar ? "end" : "space-between"}
              alignItems={"center"}
            >
              {openSidebar && (
                <Typography
                  component="h6"
                  sx={{
                    pl: 2,
                    color: colorStyling.white.full,
                    fontSize: 12,
                  }}
                >
                  {tCommon("sidebar.manageLabel")}
                </Typography>
              )}

              {minified && (
                <Button
                  sx={{
                    width: !openSidebar ? "100%" : "44px",
                    minWidth: !openSidebar ? "unset" : "44px",
                    height: "30px",
                  }}
                  variant="contained"
                  onClick={handleSidebarMenuButton}
                >
                  {!openSidebar ? (
                    <KeyboardDoubleArrowRightIcon fontSize="small" />
                  ) : (
                    <KeyboardDoubleArrowLeftIcon fontSize="small" />
                  )}
                </Button>
              )}
            </Box>

            {!openSidebar && <Divider />}

            <Box px={2}>
              <List>
                {[...sidebarMenuConfigTop, ...externalSidebarConfig]
                  .filter(
                    (item) =>
                      // Filter out items if their feature flag is in either the global or user-specific disabled features
                      (features[item.flag] || item.flag === undefined) &&
                      (!user.id ||
                        !item.condition ||
                        item.condition(userPermission, userRole, features))
                  )
                  .map((item) => menuItemUi(item))}

                {/* DIVIDER */}
                <Box
                  component="hr"
                  border={1}
                  borderColor={colorStyling.blue.light}
                  mb={2}
                />

                {sidebarMenuConfigBottom
                  .filter(
                    (item) =>
                      // Filter out items if their feature flag is in either the global or user-specific disabled features
                      (features[item.flag] || item.flag === undefined) &&
                      (!user.id ||
                        !item.condition ||
                        item.condition(userPermission, userRole))
                  )
                  .map((item) => menuItemUi(item))}
              </List>
            </Box>
          </Box>

          <Versioning textColor={"white"} />
        </StyledDrawer>
      </Box>
    </>
  );
};

export default Sidebar;
